import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Button, Field, TextInput } from "@/components/ui";
import { authError, Frame } from "./AdminAuthFlow";

function readResetToken() {
  const token =
    new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "";
  if (window.location.pathname === "/reset-password" && window.location.hash) {
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
  }
  return token;
}
const initialResetToken = readResetToken();

export function ResetPasswordPage() {
  const [token, setToken] = useState(initialResetToken);
  useEffect(() => {
    function onHashChange() {
      const next = readResetToken();
      if (next) setToken(next);
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  return <ResetFlow key={token} token={token} />;
}

function ResetFlow({ token }: { token: string }) {
  const { t } = useTranslation();
  const { invalidate } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError(t("adminAuth.passwordMismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      invalidate();
      setPassword("");
      setConfirm("");
      setDone(true);
    } catch (caught) {
      setError(authError(caught, t));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Frame>
      <div className="space-y-4">
        <h1 className="text-lg font-semibold text-ink">
          {t("adminAuth.resetTitle")}
        </h1>
        {!token ? (
          <p role="alert" className="text-sm text-danger">
            {t("adminAuth.resetMissing")}
          </p>
        ) : done ? (
          <p role="status" className="text-sm text-ink">
            {t("adminAuth.resetDone")}
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-ink-subtle">
              {t("adminAuth.resetHelp")}
            </p>
            <Field
              label={t("adminAuth.newPassword")}
              hint={t("adminAuth.passwordRules")}
            >
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </Field>
            <Field label={t("adminAuth.confirmPassword")}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              )}
            </Field>
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              loading={busy}
              disabled={
                password.length < 12 || password.length > 128 || !confirm
              }
            >
              {t("adminAuth.confirm")}
            </Button>
          </form>
        )}
        <a href="/" className="block text-sm text-brand underline">
          {t("adminAuth.back")}
        </a>
      </div>
    </Frame>
  );
}
